import type { OktaAuth } from '@okta/okta-auth-js';

import { IMPERSONATE_KEY } from 'hooks/use-impersonation';

import { ROLE_NAMES } from 'constants/roles';
import { LAST_ACTIVITY_KEY } from 'constants/session';

export const OKTA_KEY = 'okta-token-storage';
const REFFERER_PATH = 'secureRouterReferrerPath';

interface OktaTokenStorage {
  accessToken?: { accessToken?: string };
  idToken?: { claims?: { sub?: string } };
}

interface ImpersonatingUserInfo {
  oktaUserId?: string;
}

const getOktaSessionInfo = (): OktaTokenStorage | null => {
  const raw = window.localStorage.getItem(OKTA_KEY);

  if (!raw) return null;

  try {
    return JSON.parse(raw) as OktaTokenStorage;
  } catch {
    return null;
  }
};

export const clearOktaSessionInfo = (): void => {
  window.localStorage.removeItem(OKTA_KEY);
  window.localStorage.removeItem(IMPERSONATE_KEY);
  window.localStorage.removeItem(REFFERER_PATH);
  window.localStorage.removeItem(LAST_ACTIVITY_KEY);
};

export const getOktaBearerToken = (): string | null => {
  const oktaStorage = getOktaSessionInfo();
  let token: string | null = null;

  if (oktaStorage && oktaStorage.accessToken) {
    token = oktaStorage.accessToken.accessToken ?? null;
  }

  return token;
};

export const getOktaUserId = (): string | null => {
  const impersonatingRaw = window.localStorage.getItem(IMPERSONATE_KEY);
  const impersonatingUserInfo = impersonatingRaw
    ? (JSON.parse(impersonatingRaw) as ImpersonatingUserInfo)
    : null;

  if (impersonatingUserInfo) return impersonatingUserInfo.oktaUserId ?? null;

  const oktaStorage = getOktaSessionInfo();

  if (oktaStorage && oktaStorage.idToken) {
    return oktaStorage.idToken.claims?.sub ?? null;
  }

  return null;
};

export const closeOktaSession = async (oktaAuth: {
  session: Pick<OktaAuth['session'], 'close'>;
}): Promise<void> => {
  await oktaAuth.session.close().catch(() => undefined);
};

interface TokenRenewer {
  tokenManager: {
    getTokens: () => Promise<{ accessToken?: { claims?: unknown } }>;
    renew: (key: 'accessToken') => Promise<unknown>;
  };
}

export const hasMarketviewRole = (claims: unknown): boolean => {
  if (typeof claims !== 'object' || claims === null || !('roles' in claims)) {
    return false;
  }

  const { roles } = claims as { roles: unknown };

  if (!Array.isArray(roles)) return false;

  return ROLE_NAMES.some((roleName) => roles.includes(roleName));
};

let renewalInFlight: Promise<boolean> | null = null;

const renewAccessToken = async (oktaAuth: TokenRenewer): Promise<boolean> => {
  try {
    const renewed = await oktaAuth.tokenManager.renew('accessToken');

    if (typeof renewed !== 'object' || renewed === null) return false;

    return hasMarketviewRole((renewed as { claims?: unknown }).claims);
  } catch {
    return false;
  }
};

export const ensureMarketviewRole = async (
  oktaAuth: TokenRenewer
): Promise<boolean> => {
  const { accessToken } = await oktaAuth.tokenManager.getTokens();

  if (hasMarketviewRole(accessToken?.claims)) return true;

  renewalInFlight ??= renewAccessToken(oktaAuth).finally(() => {
    renewalInFlight = null;
  });

  return renewalInFlight;
};
